import React, { useState, useEffect, useContext } from 'react';
import { Plus, FileText, Calendar, DollarSign, Eye, Edit, X, Upload, AlertCircle } from 'lucide-react';
import { ClaimPortalItems, ClaimPortalManagementItems } from "../data";
import Layout from "../components/Layout";
import { Sidebar } from "../components";
import AuthContext from "../contexts/AuthContext";
import Table from "../components/Table"; // Import the Table component

const API_BASE_URL = 'https://ess-api-django-a6fc2c26f42e.herokuapp.com/api/payroll/';

const EmployeeClaimPortal = () => {
  const { authTokens, user } = useContext(AuthContext);
  const [claims, setClaims] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedClaim, setSelectedClaim] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const [showCloseConfirmModal, setShowCloseConfirmModal] = useState(false);
  const [showCloseSuccessModal, setShowCloseSuccessModal] = useState(false);
  const [closingClaimId, setClosingClaimId] = useState(null);


  const [sortConfig, setSortConfig] = useState({ key: "null", direction: "asc" });
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [rowPerPage] = useState(10);

  // Form state
  const [formData, setFormData] = useState({
    purpose: '',
    one_line_description: '',
    detailed_description: '',
    amount: '',
    attachment: null
  });
  const [errors, setErrors] = useState({});

  // Mock user data - in real app, this would come from authentication
  const currentUser = {
    id: 1,
    name: "John Doe",
    email: "john.doe@company.com"
  };

  // Fetch claims on component mount
  useEffect(() => {
    fetchClaims();
  }, []);


  // Handle sorting by a given key
const handleSort = (key) => {
  let direction = 'asc';

  if (sortConfig.key === key && sortConfig.direction === 'asc') {
    direction = 'desc';
  }

  const sortedClaims = [...claims].sort((a, b) => {
    const valA = a[key];
    const valB = b[key];

    if (typeof valA === 'string') {
      return direction === 'asc'
        ? valA.localeCompare(valB)
        : valB.localeCompare(valA);
    } else if (typeof valA === 'number') {
      return direction === 'asc' ? valA - valB : valB - valA;
    } else if (valA instanceof Date || key === 'created_at') {
      return direction === 'asc'
        ? new Date(valA) - new Date(valB)
        : new Date(valB) - new Date(valA);
    }
    return 0;
  });

  setClaims(sortedClaims);
  setSortConfig({ key, direction });
};

  const fetchClaims = async () => {
    setLoading(true);
    try {
      // Simulate API call - replace with actual API endpoint
      const response = await fetch(`${API_BASE_URL}my-claims/`, {
        headers: {
          Authorization: "Bearer " + String(authTokens.access),
        },
      });

      if (response.status === 401) {
      const hasReloaded = sessionStorage.getItem('claimPortal401Reload');
      if (!hasReloaded) {
        sessionStorage.setItem('claimPortal401Reload', 'true');
        window.location.reload();
        return;
      } else {
        sessionStorage.removeItem('claimPortal401Reload');
        throw new Error('Authentication failed. Please login again.');
      }
    }
    
      const data = await response.json();
      if (data.status === 'success') {
        setClaims(data.data);
      }
    } catch (error) {
      console.error('Error fetching claims:', error);
      // Mock data for demo
      setClaims([
        {
          id: 1,
          purpose: 'Travel Expenses',
          one_line_description: 'Business trip to Mumbai',
          detailed_description: 'Travel expenses for client meeting including flight, hotel, and meals',
          amount: 15000,
          status: 'PENDING',
          created_at: '2024-01-15T10:30:00Z',
          rejection_reason: null
        },
        {
          id: 2,
          purpose: 'Medical Reimbursement',
          one_line_description: 'Hospital bill reimbursement',
          detailed_description: 'Medical expenses for annual health check-up',
          amount: 5000,
          status: 'APPROVED',
          created_at: '2024-01-10T14:20:00Z',
          rejection_reason: null
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, files } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'file' ? files[0] : value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.purpose.trim()) newErrors.purpose = 'Purpose is required';
    if (!formData.one_line_description.trim()) newErrors.one_line_description = 'One-line description is required';
    if (!formData.detailed_description.trim()) newErrors.detailed_description = 'Detailed description is required';
    if (!formData.amount || formData.amount <= 0) newErrors.amount = 'Valid amount is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    
    setLoading(true);
    try {
      const formDataToSend = new FormData();
      Object.keys(formData).forEach(key => {
        if (formData[key] !== null && formData[key] !== '') {
          formDataToSend.append(key, formData[key]);
        }
      });

      const response = await fetch(`${API_BASE_URL}claims/`, {
        method: 'POST',
        headers: {
          Authorization: "Bearer " + String(authTokens.access),
        },
        body: formDataToSend,
      });

      const data = await response.json();
      
      if (data.status === 'success') {
        setClaims(prev => [data.data, ...prev]);
        setFormData({
          purpose: '',
          one_line_description: '',
          detailed_description: '',
          amount: '',
          attachment: null
        });
        setShowForm(false);
        setShowSuccessModal(true); // Show success modal instead of alert
      } else {
        setErrors(data.errors || {});
      }
    } catch (error) {
      console.error('Error submitting claim:', error);
      alert('Error submitting claim. Please try again.');
    } finally {
      setLoading(false);
    }
  };

 const handleCloseClaim = async () => {
    if (!closingClaimId) return;

    try {
      const response = await fetch(`${API_BASE_URL}claims/${closingClaimId}/action/`, {
        method: 'POST',
        headers: {
          Authorization: "Bearer " + String(authTokens.access),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ action: 'close' }),
      });

      const data = await response.json();
      if (data.status === 'success') {
        setClaims(prev =>
          prev.map(claim =>
            claim.id === closingClaimId ? { ...claim, status: 'CLOSED' } : claim
          )
        );
        setShowCloseConfirmModal(false);
        setShowCloseSuccessModal(true);
      }
    } catch (error) {
      console.error('Error closing claim:', error);
    } finally {
      setClosingClaimId(null);
    }
  };


  const getStatusBadge = (status) => {
    const statusColors = {
      'PENDING': 'bg-yellow-100 text-yellow-800',
      'APPROVED': 'bg-green-100 text-green-800',
      'REJECTED': 'bg-red-100 text-red-800',
      'CLOSED': 'bg-gray-100 text-gray-800'
    };
    
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[status] || 'bg-gray-100 text-gray-800'}`}>
        {status}
      </span>
    );
  };

  // Handle view claim details
  const handleViewClaim = (rowIndex) => {
    const claimIndex = (currentPage - 1) * rowPerPage + rowIndex;
    setSelectedClaim(claims[claimIndex]);
    setShowDetails(true);
  };

  // Handle close claim
  const handleCloseClaimFromTable = (rowIndex) => {
    const claimIndex = (currentPage - 1) * rowPerPage + rowIndex;
    const claim = claims[claimIndex];
    if (claim.status === 'PENDING') {
      handleCloseClaim(claim.id);
    }
  };

  // Prepare table data
  const tableHeader = [
  { label: "Purpose", key: "purpose" },
  { label: "Description", key: "one_line_description" },
  { label: "Amount", key: "amount" },
  { label: "Status", key: "status" },
  { label: "Date", key: "created_at" },
  "Actions"
];

  
  const tableBody = claims.map(claim => ({
    purpose: (
      <div>
        <div className="text-sm font-medium text-gray-900">{claim.purpose}</div>
        {claim.tentative_payment_date && (
          <div className="flex items-center gap-1 mt-1">
            <Calendar size={12} className="text-green-500" />
            <span className="text-xs text-green-600">
              Pay: {new Date(claim.tentative_payment_date).toLocaleDateString()}
            </span>
          </div>
        )}
      </div>
    ),
    description: claim.one_line_description,
    amount: `₹${claim.amount?.toLocaleString()}`,
    status: getStatusBadge(claim.status),
    date: new Date(claim.created_at).toLocaleDateString()
  }));

  // Check if claim can be closed (only PENDING claims)
  const canCloseClaim = (rowIndex) => {
    const claimIndex = (currentPage - 1) * rowPerPage + rowIndex;
    return claims[claimIndex]?.status === 'PENDING';
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  return (
    <Layout
      sidebar={
        ["MANAGER", "HR", "RMG"].includes(user.user_role) ? (
          <Sidebar sidebarLinks={ClaimPortalManagementItems} />
        ) : (
          <Sidebar sidebarLinks={ClaimPortalItems} />
        )
      }
    >
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Claim Portal</h1>
            <p className="text-gray-600">Submit and manage your claim requests</p>
          </div>

          {/* Action Button */}
          <div className="mb-6">
            <button
              onClick={() => setShowForm(true)}
              className="bg-blue-800 hover:bg-blue-900 text-white px-6 py-3 rounded-lg flex items-center gap-2 transition-colors"
            >
              <Plus size={20} />
              New Claim Request
            </button>
          </div>

          {/* Claims List */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            
            {loading ? (
              <div className="p-8 text-center">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <p className="mt-2 text-gray-600">Loading claims...</p>
              </div>
            ) : claims.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <FileText size={48} className="mx-auto mb-4 text-gray-300" />
                <p>No claims found. Submit your first claim request!</p>
              </div>
            ) : (
              <div className="transition flex flex-col items-center justify-center w-full pb-4 mb-4 mt-4">


									<div className="w-full intro-x bg-blue-900 flex rounded-t-3xl justify-center items-center py-3">
										<h4 className="mx-6 text-center table-header-text font-bold text-sm md:text-lg">
											Claim Requests
										</h4>
									</div>
              <Table
                header={tableHeader.map(h => typeof h === 'string' ? { label: h, key: h.toLowerCase().replace(/\s/g, '_') } : h)}
                body={tableBody}
                btnTitle="View"
                onClick={handleViewClaim}
                btnTitleTwo="Close"
                onClickTwo={(rowIndex) => {
                  const claimIndex = (currentPage - 1) * rowPerPage + rowIndex;
                  const claim = claims[claimIndex];
                  if (claim.status === 'PENDING') {
                    setClosingClaimId(claim.id);
                    setShowCloseConfirmModal(true);
                  }
                }}
                buttonStyleTwo={(rowIndex) => !canCloseClaim(rowIndex)}
                btnTitleTwoName={false}
                btndisTwo={(rowIndex) => !canCloseClaim(rowIndex)}
                currentPage={currentPage}
                rowPerPage={rowPerPage}
                paginationNeed={true}
                paginationBody={claims}
                onPageChange={handlePageChange}
                sortConfig={sortConfig}
                onSort={handleSort}
              />

              </div>
            )}
          </div>
          

          {/* New Claim Form Modal */}
          {showForm && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[999]">
              <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
                {/* Header */}
                <div className="bg-gradient-to-r bg-blue-900 bg-blue-900 px-6 py-4 rounded-t-xl">
                  <div className="flex justify-between items-center">
                    <div>
                      <h2 className="text-2xl font-bold text-white">New Claim Request</h2>
                      <p className="text-blue-100 text-sm mt-1">Fill out the form below to submit your claim</p>
                    </div>
                    <button
                      onClick={() => setShowForm(false)}
                      className="text-blue-100 hover:text-white transition-colors p-1"
                    >
                      <X size={24} />
                    </button>
                  </div>
                </div>

                <div className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Left Column */}
                    <div className="space-y-6">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          <span className="flex items-center gap-2">
                            <FileText size={16} />
                            Purpose *
                          </span>
                        </label>
                        <input
                          type="text"
                          name="purpose"
                          value={formData.purpose}
                          onChange={handleInputChange}
                          className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all ${
                            errors.purpose ? 'border-red-500 bg-red-50' : 'border-gray-300 hover:border-gray-400'
                          }`}
                          placeholder="e.g., Travel Expenses, Medical Reimbursement"
                        />
                        {errors.purpose && (
                          <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                            <AlertCircle size={16} />
                            {errors.purpose}
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          <span className="flex items-center gap-2">
                            <Edit size={16} />
                            One-line Description *
                          </span>
                        </label>
                        <input
                          type="text"
                          name="one_line_description"
                          value={formData.one_line_description}
                          onChange={handleInputChange}
                          className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all ${
                            errors.one_line_description ? 'border-red-500 bg-red-50' : 'border-gray-300 hover:border-gray-400'
                          }`}
                          placeholder="Brief summary of your claim"
                        />
                        {errors.one_line_description && (
                          <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                            <AlertCircle size={16} />
                            {errors.one_line_description}
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          <span className="flex items-center gap-2">
                            <DollarSign size={16} />
                            Amount (₹) *
                          </span>
                        </label>
                        <input
                          type="number"
                          name="amount"
                          value={formData.amount}
                          onChange={handleInputChange}
                          min="0"
                          step="0.01"
                          className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all ${
                            errors.amount ? 'border-red-500 bg-red-50' : 'border-gray-300 hover:border-gray-400'
                          }`}
                          placeholder="Enter claim amount"
                        />
                        {errors.amount && (
                          <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                            <AlertCircle size={16} />
                            {errors.amount}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Right Column */}
                    <div className="space-y-6">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          <span className="flex items-center gap-2">
                            <FileText size={16} />
                            Detailed Description *
                          </span>
                        </label>
                        <textarea
                          name="detailed_description"
                          value={formData.detailed_description}
                          onChange={handleInputChange}
                          rows={6}
                          className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all resize-none ${
                            errors.detailed_description ? 'border-red-500 bg-red-50' : 'border-gray-300 hover:border-gray-400'
                          }`}
                          placeholder="Provide detailed explanation of your claim including dates, locations, and receipts if applicable..."
                        />
                        {errors.detailed_description && (
                          <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                            <AlertCircle size={16} />
                            {errors.detailed_description}
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          <span className="flex items-center gap-2">
                            <Upload size={16} />
                            Attachment (Optional)
                          </span>
                        </label>
                        <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 hover:border-blue-400 transition-colors bg-gray-50 hover:bg-blue-50">
                          <input
                            type="file"
                            name="attachment"
                            onChange={handleInputChange}
                            className="hidden"
                            id="file-upload"
                            accept=".pdf,.jpg,.jpeg,.png"
                          />
                          <label
                            htmlFor="file-upload"
                            className="cursor-pointer flex flex-col items-center justify-center"
                          >
                            <div className="p-3 bg-blue-100 rounded-full mb-3">
                              <Upload size={24} className="text-blue-600" />
                            </div>
                            <span className="text-sm font-medium text-gray-700 mb-1">
                              {formData.attachment ? (
                                <span className="text-blue-600">{formData.attachment.name}</span>
                              ) : (
                                'Click to upload file'
                              )}
                            </span>
                            <span className="text-xs text-gray-500">
                              PDF, JPG, PNG (Max 10MB)
                            </span>
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Form Actions */}
                  <div className="flex gap-4 pt-8 border-t border-gray-200 mt-8">
                    <button
                      type="button"
                      onClick={handleSubmit}
                      disabled={loading}
                      className="flex-1 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white py-3 px-6 rounded-lg font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {loading ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          Submitting...
                        </>
                      ) : (
                        <>
                          <Plus size={16} />
                          Submit Claim
                        </>
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowForm(false)}
                      className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 py-3 px-6 rounded-lg font-semibold transition-all flex items-center justify-center gap-2"
                    >
                      <X size={16} />
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
          {showSuccessModal && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                <div className="bg-white rounded-xl max-w-md w-full shadow-2xl transform transition-all">
                  {/* Success Header */}
                  <div className="bg-gradient-to-r from-blue-900 to-blue-900 px-6 py-4 rounded-t-xl">
                    <div className="flex items-center gap-3">
                      <div className="bg-white bg-opacity-20 p-2 rounded-full">
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <h2 className="text-xl font-bold text-white">Success!</h2>
                    </div>
                  </div>

                  {/* Success Content */}
                  <div className="p-6">
                    <div className="text-center">
                      <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-4">
                        <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">Claim Request Submitted!</h3>
                      <p className="text-gray-600 mb-6">
                        Your claim request has been submitted successfully and is now pending review.
                      </p>
                      <button
                        onClick={() => setShowSuccessModal(false)}
                        className="w-full bg-blue-900 hover:bg-blue-900 text-white py-3 px-6 rounded-lg font-semibold transition-colors"
                      >
                        Continue
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {showCloseConfirmModal && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                <div className="bg-white rounded-xl max-w-md w-full shadow-2xl">
                  <div className="bg-gradient-to-r from-blue-900 to-blue-900 px-6 py-4 rounded-t-xl">
                    <h2 className="text-xl font-bold text-white">Confirm Close</h2>
                  </div>
                  <div className="p-6 text-center">
                    <p className="text-gray-700 mb-6">
                      Are you sure you want to close this claim? This action cannot be undone.
                    </p>
                    <div className="flex gap-4">
                      <button
                        onClick={handleCloseClaim}
                        className="flex-1 bg-red-600 hover:bg-red-700 text-white py-2 px-4 rounded-lg font-semibold transition-colors"
                      >
                        Yes, Close
                      </button>
                      <button
                        onClick={() => {
                          setShowCloseConfirmModal(false);
                          setClosingClaimId(null);
                        }}
                        className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 py-2 px-4 rounded-lg font-semibold transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {showCloseSuccessModal && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                <div className="bg-white rounded-xl max-w-md w-full shadow-2xl">
                  <div className="bg-gradient-to-r from-blue-900 to-blue-900 px-6 py-4 rounded-t-xl">
                    <h2 className="text-xl font-bold text-white">Success!</h2>
                  </div>
                  <div className="p-6 text-center">
                    <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-4">
                      <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Claim Closed!</h3>
                    <p className="text-gray-600 mb-6">
                      Your claim has been successfully closed.
                    </p>
                    <button
                      onClick={() => setShowCloseSuccessModal(false)}
                      className="w-full bg-blue-900 hover:bg-blue-800 text-white py-3 px-6 rounded-lg font-semibold transition-colors"
                    >
                      Continue
                    </button>
                  </div>
                </div>
              </div>
            )}

          {/* Claim Details Modal */}
          {showDetails && selectedClaim && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
              <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
                {/* Header */}
                <div className="bg-gradient-to-r bg-blue-900 to-purple-700 px-6 py-4 rounded-t-xl">
                  <div className="flex justify-between items-center">
                    <div>
                      <h2 className="text-2xl font-bold text-white">Claim Details</h2>
                      <p className="text-purple-100 text-sm mt-1">View complete claim information</p>
                    </div>
                    <button
                      onClick={() => setShowDetails(false)}
                      className="text-purple-100 hover:text-white transition-colors p-1"
                    >
                      <X size={24} />
                    </button>
                  </div>
                </div>

                <div className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Left Column */}
                    <div className="space-y-6">
                      <div className="bg-gray-50 p-4 rounded-lg border">
                        <label className="block text-sm font-semibold text-gray-600 mb-2">
                          <span className="flex items-center gap-2">
                            <FileText size={16} />
                            Purpose
                          </span>
                        </label>
                        <p className="text-gray-900 font-medium text-lg">{selectedClaim.purpose}</p>
                      </div>

                      <div className="bg-gray-50 p-4 rounded-lg border">
                        <label className="block text-sm font-semibold text-gray-600 mb-2">
                          <span className="flex items-center gap-2">
                            <Edit size={16} />
                            One-line Description
                          </span>
                        </label>
                        <p className="text-gray-900">{selectedClaim.one_line_description}</p>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                          <label className="block text-sm font-semibold text-blue-700 mb-2">
                            <span className="flex items-center gap-2">
                              <DollarSign size={16} />
                              Amount
                            </span>
                          </label>
                          <p className="text-blue-900 font-bold text-xl">₹{selectedClaim.amount?.toLocaleString()}</p>
                        </div>
                        <div className="bg-gray-50 p-4 rounded-lg border">
                          <label className="block text-sm font-semibold text-gray-600 mb-2">
                            Status
                          </label>
                          <div className="mt-1">{getStatusBadge(selectedClaim.status)}</div>
                        </div>
                      </div>

                      <div className="bg-gray-50 p-4 rounded-lg border">
                        <label className="block text-sm font-semibold text-gray-600 mb-2">
                          <span className="flex items-center gap-2">
                            <Calendar size={16} />
                            Created Date
                          </span>
                        </label>
                        <p className="text-gray-900 font-medium">{new Date(selectedClaim.created_at).toLocaleString()}</p>
                      </div>

                      {/* Payment Date Display */}
                      {selectedClaim.tentative_payment_date && (
                        <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                          <label className="block text-sm font-semibold text-green-700 mb-2">
                            <span className="flex items-center gap-2">
                              <Calendar size={16} />
                              Tentative Payment Date
                            </span>
                          </label>
                          <p className="text-green-900 font-bold text-lg">
                            {new Date(selectedClaim.tentative_payment_date).toLocaleDateString('en-IN', {
                              day: 'numeric',
                              month: 'long',
                              year: 'numeric'
                            })}
                          </p>
                          {selectedClaim.payment_date_set_by_name && (
                            <p className="text-xs text-green-600 mt-1">
                              Set by {selectedClaim.payment_date_set_by_name}
                            </p>
                          )}
                        </div>
                      )}

                      {selectedClaim.closed_at && selectedClaim.status === 'CLOSED' && (
                        <div className="bg-gray-50 p-4 rounded-lg border">
                          <label className="block text-sm font-semibold text-gray-600 mb-2">
                            <span className="flex items-center gap-2">
                              <Calendar size={16} />
                              Closed Date
                            </span>
                          </label>
                          <p className="text-gray-900 font-medium">{new Date(selectedClaim.closed_at).toLocaleString()}</p>
                        </div>
                      )}
                    </div>

                    {/* Right Column */}
                    <div className="space-y-6">
                      <div className="bg-gray-50 p-4 rounded-lg border h-full">
                        <label className="block text-sm font-semibold text-gray-600 mb-2">
                          <span className="flex items-center gap-2">
                            <FileText size={16} />
                            Detailed Description
                          </span>
                        </label>
                        <div className="bg-white p-3 rounded border min-h-[200px]">
                          <p className="text-gray-900 whitespace-pre-wrap leading-relaxed">{selectedClaim.detailed_description}</p>
                        </div>
                      </div>

                      {selectedClaim.rejection_reason && (
                        <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                          <label className="block text-sm font-semibold text-red-700 mb-2">
                            <span className="flex items-center gap-2">
                              <AlertCircle size={16} />
                              Rejection Reason
                            </span>
                          </label>
                          <div className="bg-white p-3 rounded border border-red-200">
                            <p className="text-red-900">{selectedClaim.rejection_reason}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Modal Actions */}
                  <div className="flex justify-end gap-3 pt-8 border-t border-gray-200 mt-8">
                    {selectedClaim.status === 'PENDING' && (
                      <button
                        onClick={() => {
                          setClosingClaimId(selectedClaim.id);
                          setShowCloseConfirmModal(true);
                          setShowDetails(false);
                        }}
                        className="bg-red-600 hover:bg-red-700 text-white py-2 px-4 rounded-lg font-medium transition-colors flex items-center gap-2"
                      >
                        <X size={16} />
                        Close Claim
                      </button>
                    )}
                    <button
                      onClick={() => setShowDetails(false)}
                      className="bg-gray-100 hover:bg-gray-200 text-gray-700 py-2 px-4 rounded-lg font-medium transition-colors flex items-center gap-2"
                    >
                      <Eye size={16} />
                      Close View
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default EmployeeClaimPortal;